/**
 * Lesson MCQ Player
 * Split-view player with lesson content on left and MCQ questions on right
 * Uses SplitPanel for resizable panels with individual scrolling
 * Responsive: stacks vertically on small screens
 */

import * as React from 'react';
import { cn, Card, useIsMobile } from '@ai-content-hub/content-kit';
import { isCorrectAnswerRevealed, useLocale } from '@ai-content-hub/content-core';
import type { MCQAnswer } from '@ai-content-hub/content-elements';
import { PagePlayer, MCQSetPlayer, type PageElementData } from '@ai-content-hub/content-elements';
import { RichTextInline } from '@ai-content-hub/content-elements';
import { SplitPanel } from '@ai-content-hub/shared-components';
import type { LessonMCQData, LessonMCQConfig, LessonMCQLayoutConfig, SplitRatio } from './schema';
import { defaultLessonMCQData } from './schema';
import { BookOpen, ListChecks } from 'lucide-react';

/**
 * Map split ratio to left panel width percentage for SplitPanel
 */
function getInitialLeftWidth(ratio: SplitRatio): number {
    switch (ratio) {
        case '50-50':
            return 50;
        case '60-40':
            return 60;
        case '40-60':
            return 40;
        case '70-30':
            return 70;
        case '30-70':
            return 30;
        default:
            return 60;
    }
}

function embeddedMcqSetLayout(layoutConfig: LessonMCQLayoutConfig | undefined) {
    const raw = layoutConfig?.displayMode ?? 'all';
    const displayMode = raw === 'step' ? 'tabs' : raw;
    return {
        type: 'vertical' as const,
        displayMode,
        showProgress: true,
        showQuestionNumbers: true,
    };
}

/** PagePlayer shows lesson sections only; root Instructions render above the layout. */
function mergeLessonContentForPlayer(data: LessonMCQData): PageElementData {
    const lessonContent = data.lessonContent ?? defaultLessonMCQData.lessonContent;
    const rootDescription = data.description?.trim();
    return {
        ...lessonContent,
        title: undefined,
        description: rootDescription ? undefined : lessonContent.description,
    };
}

export interface LessonMCQPlayerProps {
    /** Content data */
    data: LessonMCQData;
    /** Config */
    config?: LessonMCQConfig;
    /** Called when an answer changes */
    onAnswer?: (answer: unknown) => void;
    /** Controlled MCQ answers (ContentPlayer) */
    answer?: Record<string, MCQAnswer>;
    /** Grading result from ContentPlayer after submit */
    result?: {
        isCorrect?: boolean | null;
        score?: number;
        max?: number;
        details?: Record<string, unknown>;
    };
    /** Called when questions are submitted */
    onSubmit?: (results: unknown) => void;
    /** Called when content is completed */
    onComplete?: (results: unknown) => void;
    /** Show feedback after submission */
    showFeedback?: boolean;
    /** In review mode */
    isReview?: boolean;
    /** Interaction disabled */
    disabled?: boolean;
    /** Additional class name */
    className?: string;
}

export const LessonMCQPlayer: React.FC<LessonMCQPlayerProps> = ({
    data,
    config,
    onAnswer,
    answer,
    result,
    showFeedback,
    isReview = false,
    disabled = false,
    className,
}) => {
    const layoutConfig = config?.layout as LessonMCQLayoutConfig | undefined;
    const i18nConfig = config?.i18n;

    const { t } = useLocale({ config: i18nConfig });
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false.
    const revealCorrect = isCorrectAnswerRevealed(config);

    const splitRatio = layoutConfig?.splitRatio ?? '50-50';
    const minHeight = layoutConfig?.minHeight ?? '600px';
    const responsive = layoutConfig?.responsive !== false;
    const panelLayoutType = layoutConfig?.type ?? 'auto';
    const isAutoPanelLayout = panelLayoutType === 'auto';

    const isMobile = useIsMobile(768);
    const showSplitLayout = isAutoPanelLayout ? !isMobile : !responsive || !isMobile;
    const mcqSetLayoutBlock = embeddedMcqSetLayout(layoutConfig);

    // Ensure data has defaults
    const safeData: LessonMCQData = {
        ...defaultLessonMCQData,
        ...data,
        lessonContent: data?.lessonContent ?? defaultLessonMCQData.lessonContent,
        questionSet: data?.questionSet ?? defaultLessonMCQData.questionSet,
    };

    const hasLessonContent = (safeData.lessonContent?.sections?.length ?? 0) > 0;
    const hasRootDescription = Boolean(safeData.description?.trim());
    const lessonContentForPlayer = mergeLessonContentForPlayer(safeData);

    const instructionsHeader = hasRootDescription ? (
        <div className="lesson-mcq-player-header lesson-mcq-player-header--intro flex-shrink-0 space-y-2">
            <RichTextInline
                text={safeData.description ?? ''}
                as="p"
                className="lesson-mcq-player-description text-sm text-muted-foreground leading-relaxed"
            />
        </div>
    ) : null;

    const handleAnswer = (next: unknown) => {
        onAnswer?.(next);
    };

    const renderStackedPanels = () => (
        <div
            className={cn('lesson-mcq-player lesson-mcq-player--stacked flex flex-col', className)}
            style={{ minHeight }}
        >
            {instructionsHeader}
            <div className="lesson-mcq-player-stacked-layout flex-1 flex flex-col gap-6 overflow-y-auto">
                {hasLessonContent && (
                    <div className="lesson-mcq-player-lesson-section flex-shrink-0">
                        <Card className="lesson-mcq-player-lesson-section-card border-0 rounded-b-none">
                            <div className="lesson-mcq-player-lesson-section-body">
                                <PagePlayer
                                    data={lessonContentForPlayer}
                                    submitted={showFeedback && revealCorrect}
                                />
                            </div>
                        </Card>
                    </div>
                )}

                <div className="lesson-mcq-player-questions-section flex-shrink-0">
                    <div className="lesson-mcq-player-questions-section-card border-0">
                        <div className="lesson-mcq-player-questions-section-body p-2">
                            <MCQSetPlayer
                                data={safeData.questionSet}
                                config={{
                                    behavior: config?.behavior,
                                    scoring: config?.scoring,
                                    i18n: config?.i18n,
                                    layout: mcqSetLayoutBlock,
                                }}
                                onAnswer={handleAnswer}
                                answer={answer}
                                result={result}
                                showFeedback={showFeedback}
                                isReview={isReview}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!data) {
        return (
            <div
                className={cn(
                    'lesson-mcq-player lesson-mcq-player--empty text-muted-foreground italic p-4',
                    className
                )}
            >
                {t('noContent')}
            </div>
        );
    }

    // Render stacked/flat layout for mobile
    if (!showSplitLayout) {
        return renderStackedPanels();
    }

    // Desktop: explicit vertical panel arrangement (stacked)
    if (panelLayoutType === 'vertical' && hasLessonContent) {
        return renderStackedPanels();
    }

    // Single-panel layout when no lesson content
    if (!hasLessonContent) {
        return (
            <div
                className={cn(
                    'lesson-mcq-player lesson-mcq-player--split flex flex-col h-full',
                    className
                )}
                style={{ minHeight, height: '100%' }}
            >
                <div className="lesson-mcq-player-split-layout flex-1 min-h-0 flex flex-col p-2">
                    <div className="lesson-mcq-player-questions-panel flex flex-col flex-1 min-h-0 overflow-hidden">
                        <div className="lesson-mcq-player-questions-panel-card flex-1 min-h-0 overflow-hidden">
                            <div className="lesson-mcq-player-questions-panel-body h-full overflow-y-auto">
                                <MCQSetPlayer
                                    data={safeData.questionSet}
                                    config={{
                                        behavior: config?.behavior,
                                        scoring: config?.scoring,
                                        i18n: config?.i18n,
                                        layout: mcqSetLayoutBlock,
                                    }}
                                    onAnswer={handleAnswer}
                                    answer={answer}
                                    result={result}
                                    showFeedback={showFeedback}
                                    isReview={isReview}
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const leftContent = (
        <div className="lesson-mcq-player-lesson-panel pr-3 mb-1 flex flex-col flex-1 min-h-0 overflow-hidden">
            <Card className="lesson-mcq-player-lesson-panel-card flex-1 min-h-0 overflow-hidden flex flex-col">
                <div className="lesson-mcq-player-lesson-panel-body flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 overscroll-contain">
                    <PagePlayer data={lessonContentForPlayer} submitted={showFeedback && revealCorrect} />
                </div>
            </Card>
        </div>
    );

    const rightContent = (
        <div className="lesson-mcq-player-questions-panel flex flex-col flex-1 min-h-0 overflow-hidden pl-3 mb-1 bg-muted/5">
            <Card className="lesson-mcq-player-questions-panel-card p-2 pb-4 flex-1 min-h-0 overflow-hidden flex flex-col">
                <div className="lesson-mcq-player-questions-panel-body flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
                    <MCQSetPlayer
                        data={safeData.questionSet}
                        config={{
                            behavior: config?.behavior,
                            scoring: config?.scoring,
                            i18n: config?.i18n,
                            layout: mcqSetLayoutBlock,
                        }}
                        onAnswer={handleAnswer}
                        answer={answer}
                        result={result}
                        showFeedback={showFeedback}
                        isReview={isReview}
                        disabled={disabled}
                    />
                </div>
            </Card>
        </div>
    );

    return (
        <div
            className={cn(
                'lesson-mcq-player lesson-mcq-player--split flex flex-col h-full',
                className
            )}
            style={{ minHeight, height: '100%' }}
        >
            {instructionsHeader}
            <div className="lesson-mcq-player-split-layout flex-1 min-h-0">
                <SplitPanel
                    leftContent={leftContent}
                    rightContent={rightContent}
                    showRight={true}
                    initialLeftWidth={getInitialLeftWidth(splitRatio)}
                    classPrefix="lesson-mcq-player-split"
                    style={{ height: '100%' }}
                />
            </div>
        </div>
    );
};

export default LessonMCQPlayer;
